import { brandingToTokens, type TenantBranding } from '@auctisync/color';
import { formatCents, type Tables } from '@auctisync/shared';
import type { AuctionRow } from '../lib/rowColumns';
import type { CSSProperties, ReactNode } from 'react';

/**
 * PrintableInvoice — the tenant-branded customer invoice / paid receipt for the
 * browser print dialog / Save-as-PDF. Rebuilt to the Claude Design customer-
 * invoice handoff (`docs/design-handoff` bundle): a white header, Bill-to /
 * Remit-to, a status amount box with a DUE/PAID badge + signature line, a red
 * ops PICKUP BAND (buyer last name + auction #, always red, high on page 1),
 * an auction strip, an itemized lot table, totals, pickup/pay cards, terms, and
 * a footer. Renders NO app chrome; import once on a standalone print route.
 *
 * Data comes from the props the route loads:
 *   • invoice / lines / auction — public.invoices / invoice_line_items / auctions
 *   • printContext — public.get_invoice_print_context(): tenant display_name +
 *     metadata->'branding' + metadata->'invoice_settings'
 *   • buyer — name / last_name / email / paddle / address (name+address come
 *     from the print-context RPC, gated to owner/staff/super_admin)
 *
 * Unlike the design's reference (which recomputes premium/tax from a global
 * percentage), we feed the REAL stored per-line and aggregate cents from our
 * schema — the printed columns are the authoritative amounts, not a recompute.
 *
 * Branding: the tenant's --brand-primary / --brand-accent are applied as CSS
 * custom properties on the wrapper (falling back to the app tokens), so a
 * tenant's colors flow through even outside the white-label CSS pipeline.
 */

type Invoice = Tables<'invoices'>;
type Line = Tables<'invoice_line_items'>;
type Auction = AuctionRow;

export interface InvoicePrintContext {
  tenant_id: string;
  display_name: string;
  branding: TenantBrandingBlob;
  invoice_settings: InvoiceSettingsBlob;
}

interface TenantBrandingBlob {
  logo_url?: string;
  primary_color?: string;
  secondary_color?: string;
  accent_color?: string;
  support_email?: string;
}

export interface AddressBlob {
  line1?: string | undefined;
  line2?: string | undefined;
  city?: string | undefined;
  region?: string | undefined;
  postal_code?: string | undefined;
  country?: string | undefined;
}

interface InvoiceSettingsBlob {
  make_checks_payable_to?: string;
  remit_to?: AddressBlob;
  check_instructions?: string;
  pickup_default?: {
    location?: string;
    address?: AddressBlob;
    instructions?: string;
    dates_text?: string;
  };
  terms_and_conditions?: string;
  invoice_footer?: string;
  thank_you_note?: string;
  show_tax_column?: boolean;
}

export interface InvoiceBuyer {
  /** Full name (first + last), for Bill-to. */
  name?: string | null;
  /** Last name only, for the large red pickup identifier. */
  last_name?: string | null;
  email?: string | null;
  /** Paddle / tenant bidder number. */
  bidder_number?: number | null;
  address?: AddressBlob | null;
}

/** Ops pickup label is ALWAYS red — it's an identifier, not a status. */
const PICKUP_RED = 'var(--semantic-closing, #dc2626)';
const MONO = "var(--font-mono, 'Geist Mono', ui-monospace, 'SF Mono', monospace)";
const HEAD = 'var(--font-heading)';

const microLabel: CSSProperties = {
  font: `600 10px/1.4 ${HEAD}`,
  letterSpacing: '0.09em',
  textTransform: 'uppercase',
  color: 'var(--text-muted, #525252)',
};
const money: CSSProperties = { fontFamily: HEAD, fontVariantNumeric: 'tabular-nums' };

// ─── Formatting ──────────────────────────────────────────────────────────────

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** bps → percent string, trimming trailing zeros: 1500 -> "15", 825 -> "8.25". */
function bpsToPct(bps: number | null | undefined): string | null {
  if (!bps || bps <= 0) return null;
  return String(Math.round((bps / 100) * 100) / 100);
}

/** Effective tax rate on (hammer + premium), for the totals label. */
function effectiveTaxPct(taxCents: number, baseCents: number): string | null {
  if (taxCents <= 0 || baseCents <= 0) return null;
  return (Math.round((taxCents / baseCents) * 10000) / 100).toFixed(2).replace(/\.?0+$/, '');
}

// ─── Component ───────────────────────────────────────────────────────────────

export function PrintableInvoice({
  invoice,
  lines,
  auction,
  printContext,
  buyer,
}: {
  invoice: Invoice;
  lines: Line[];
  auction: Auction | null;
  printContext: InvoicePrintContext | null;
  buyer?: InvoiceBuyer | null;
}) {
  const settings = printContext?.invoice_settings ?? {};
  const branding = printContext?.branding ?? {};
  const houseName = printContext?.display_name ?? 'Auction house';
  const showTax = settings.show_tax_column !== false;

  const paid = invoice.status === 'paid';
  const statusColor = paid
    ? 'var(--semantic-winning, #059669)'
    : 'var(--semantic-closing, #dc2626)';

  // Tenant brand vars on the wrapper via the canonical AA-guaranteed mapping
  // (single source of truth). Empty when the tenant set no primary color →
  // falls back to the app --brand-* defaults in the print styles below.
  const wrapperVars = brandingToTokens(branding as TenantBranding, 'light') as CSSProperties;

  // Buyer.
  const buyerName = (buyer?.name ?? '').trim();
  const lastName = (buyer?.last_name ?? buyerName.split(/\s+/).pop() ?? '').toUpperCase() || '—';
  const buyerAddr = buyer?.address ?? null;

  // Auction context.
  const auctionNo = auction?.reference_number ? `#${auction.reference_number}` : '—';
  const premiumPct = bpsToPct(
    (auction?.buyers_premium_bps ?? 0) + (auction?.online_premium_bps ?? 0),
  );

  // Totals (real stored cents).
  const hammerCents = invoice.subtotal_cents ?? 0;
  const premiumCents = (invoice.buyers_premium_cents ?? 0) + (invoice.online_premium_cents ?? 0);
  const taxCents = invoice.sales_tax_cents ?? 0;
  const shippingCents = invoice.shipping_cents ?? 0;
  const grandCents = invoice.total_cents ?? 0;
  const taxPct = effectiveTaxPct(taxCents, hammerCents + premiumCents);

  // Remit / checks.
  const remit = settings.remit_to;
  const checksPayable = settings.make_checks_payable_to?.trim() || houseName;
  const hasRemit = Boolean(
    settings.make_checks_payable_to ||
    settings.check_instructions ||
    (remit && hasAnyAddressField(remit)),
  );

  const pickup = resolvePickup(invoice, auction, settings.pickup_default);
  const tenantAddr: AddressBlob = remit ?? {};
  const supportEmail = branding.support_email?.trim();
  const terms = splitParagraphs(settings.terms_and_conditions);
  const footerNote = settings.invoice_footer?.trim();
  const thankYou = settings.thank_you_note?.trim();

  const statusLabel = paid ? 'paid' : 'payment due';
  const amountLabel = paid ? 'Paid in full' : 'Amount due';
  const statusSub = paid ? `Paid ${fmtDate(invoice.paid_at)}` : `Due ${fmtDate(invoice.due_at)}`;

  return (
    <div className="invoice-desk" style={{ ...wrapperVars, ...deskStyle }}>
      <style>{PRINT_CSS}</style>
      <div className="invoice-sheet" style={sheetStyle}>
        {/* HEADER BAND */}
        <header style={headerStyle}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <div style={markStyle}>{houseName.charAt(0).toUpperCase()}</div>
            <div>
              <div style={{ font: `700 19px/1.1 ${HEAD}`, letterSpacing: '-0.01em' }}>
                {houseName}
              </div>
              <div
                style={{
                  fontSize: 12,
                  lineHeight: 1.55,
                  color: 'var(--text-muted, #525252)',
                  marginTop: 5,
                }}
              >
                {addressOneLine(tenantAddr)}
                {supportEmail ? (
                  <>
                    <br />
                    {supportEmail}
                  </>
                ) : null}
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'right', flex: 'none' }}>
            <div
              style={{
                font: `800 25px/1 ${HEAD}`,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
              }}
            >
              {paid ? 'Receipt' : 'Invoice'}
            </div>
            <div
              style={{
                fontFamily: MONO,
                fontSize: 12.5,
                color: 'var(--text-muted, #525252)',
                marginTop: 7,
              }}
            >
              #{invoice.invoice_number}
            </div>
            <div style={{ display: 'flex', gap: 24, justifyContent: 'flex-end', marginTop: 14 }}>
              <HeaderMeta label="Issued" value={fmtDate(invoice.issued_at)} />
              <HeaderMeta label="Due" value={fmtDate(invoice.due_at)} />
            </div>
          </div>
        </header>

        <div style={{ padding: '26px 40px 32px' }}>
          {/* Parties + amount box */}
          <div style={{ display: 'flex', gap: 28, alignItems: 'flex-start', marginBottom: 22 }}>
            <div style={{ flex: '1 1 300px', display: 'flex', gap: 30 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={microLabel}>Bill to</div>
                <div style={{ font: `600 15px ${HEAD}`, marginTop: 9 }}>
                  {buyerName || `Bidder ${buyer?.bidder_number ?? ''}`.trim()}
                </div>
                <div
                  style={{
                    fontSize: 12.5,
                    lineHeight: 1.55,
                    color: 'var(--text-muted, #525252)',
                    marginTop: 3,
                  }}
                >
                  {buyerAddr ? <AddressLines address={buyerAddr} /> : null}
                  {buyer?.email ? <div>{buyer.email}</div> : null}
                </div>
                {buyer?.bidder_number != null && (
                  <div
                    style={{
                      fontFamily: MONO,
                      fontSize: 11,
                      color: 'var(--text-muted, #525252)',
                      marginTop: 7,
                    }}
                  >
                    Paddle {buyer.bidder_number}
                  </div>
                )}
              </div>
              {hasRemit && (
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={microLabel}>Remit payment to</div>
                  <div style={{ font: `600 15px ${HEAD}`, marginTop: 9 }}>{checksPayable}</div>
                  <div
                    style={{
                      fontSize: 12.5,
                      lineHeight: 1.55,
                      color: 'var(--text-muted, #525252)',
                      marginTop: 3,
                    }}
                  >
                    {remit ? <AddressLines address={remit} /> : null}
                  </div>
                  <div
                    style={{
                      fontFamily: MONO,
                      fontSize: 11,
                      color: 'var(--text-muted, #525252)',
                      marginTop: 7,
                    }}
                  >
                    Checks payable to {checksPayable}
                  </div>
                </div>
              )}
            </div>

            {/* Amount box */}
            <div style={amountBoxStyle}>
              <div style={{ height: 5, background: statusColor }} />
              <div style={{ padding: '15px 18px 17px' }}>
                <div style={microLabel}>{amountLabel}</div>
                <div
                  style={{
                    font: `800 30px/1 ${HEAD}`,
                    letterSpacing: '-0.02em',
                    fontVariantNumeric: 'tabular-nums',
                    margin: '6px 0 12px',
                  }}
                >
                  {formatCents(grandCents)}
                </div>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 9,
                    padding: '9px 17px',
                    borderRadius: 9,
                    background: statusColor,
                    color: '#fff',
                    font: `800 16px ${HEAD}`,
                    letterSpacing: '0.07em',
                    textTransform: 'uppercase',
                  }}
                >
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} />
                  {statusLabel}
                </div>
                <div
                  style={{
                    fontFamily: MONO,
                    fontSize: 11,
                    color: 'var(--text-muted, #525252)',
                    marginTop: 12,
                  }}
                >
                  {statusSub}
                </div>
                <div
                  style={{
                    marginTop: 14,
                    paddingTop: 14,
                    borderTop: '1px solid var(--border, #e5e5e5)',
                  }}
                >
                  <div style={{ height: 30, borderBottom: '1px solid var(--text, #0a0a0a)' }} />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 10,
                      marginTop: 6,
                    }}
                  >
                    <span style={sigLabel}>Customer signature</span>
                    <span style={sigLabel}>Date</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* PICKUP BAND — always red, high on page 1 */}
          <div className="invoice-avoid" style={pickupBandStyle}>
            <div style={{ flex: '1 1 auto', minWidth: 0 }}>
              <div
                style={{
                  font: `600 9.5px/1.4 ${HEAD}`,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: PICKUP_RED,
                }}
              >
                For pickup — present at counter
              </div>
              <div
                style={{
                  font: `800 50px/0.95 ${HEAD}`,
                  letterSpacing: '-0.02em',
                  textTransform: 'uppercase',
                  color: PICKUP_RED,
                  marginTop: 2,
                }}
              >
                {lastName}
              </div>
            </div>
            <div style={{ textAlign: 'right', flex: 'none' }}>
              <div
                style={{
                  font: `600 9.5px/1.4 ${HEAD}`,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: 'var(--text-muted, #525252)',
                }}
              >
                Auction
              </div>
              <div
                style={{
                  font: `800 34px/1 ${HEAD}`,
                  letterSpacing: '-0.01em',
                  color: PICKUP_RED,
                  fontVariantNumeric: 'tabular-nums',
                  marginTop: 3,
                }}
              >
                {auctionNo}
              </div>
            </div>
          </div>

          {/* Auction strip */}
          <div style={stripStyle}>
            <MetaBlock
              label="Auction"
              value={
                auction?.reference_number
                  ? `${auction.title ?? 'Auction'} ${auctionNo}`
                  : (auction?.title ?? 'Auction')
              }
            />
            <MetaBlock label="Closed" value={fmtDateTime(auction?.ends_at)} />
            <MetaBlock
              label="Lots won"
              value={`${lines.length} ${lines.length === 1 ? 'lot' : 'lots'}`}
            />
            {premiumPct && <MetaBlock label="Buyer's premium" value={`${premiumPct}%`} />}
          </div>

          {/* Line items */}
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <Th width={80}>Lot</Th>
                <Th>Description</Th>
                <Th align="right" width={86}>
                  Hammer
                </Th>
                <Th align="right" width={90}>
                  Premium
                </Th>
                {showTax && (
                  <Th align="right" width={72}>
                    Tax
                  </Th>
                )}
                <Th align="right" width={96}>
                  Line total
                </Th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const cell: CSSProperties = {
                  padding: '11px 0 11px 8px',
                  verticalAlign: 'top',
                  textAlign: 'right',
                  ...money,
                  fontWeight: 500,
                  fontSize: 12.5,
                  borderBottom: '1px solid var(--border, #e5e5e5)',
                };
                return (
                  <tr key={l.id} className="invoice-row">
                    <td
                      style={{
                        padding: '11px 10px 11px 0',
                        verticalAlign: 'top',
                        fontFamily: MONO,
                        fontSize: 11,
                        color: 'var(--text-muted, #525252)',
                        borderBottom: '1px solid var(--border, #e5e5e5)',
                      }}
                    >
                      {l.lot_number ?? '—'}
                    </td>
                    <td
                      style={{
                        padding: '11px 10px 11px 0',
                        verticalAlign: 'top',
                        fontSize: 13,
                        lineHeight: 1.4,
                        fontWeight: 500,
                        borderBottom: '1px solid var(--border, #e5e5e5)',
                      }}
                    >
                      {l.description}
                    </td>
                    <td style={cell}>{formatCents(l.hammer_cents ?? 0)}</td>
                    <td style={{ ...cell, color: 'var(--text-muted, #525252)' }}>
                      {formatCents(l.buyers_premium_cents ?? 0)}
                    </td>
                    {showTax && (
                      <td style={{ ...cell, color: 'var(--text-muted, #525252)' }}>
                        {formatCents(l.sales_tax_cents ?? 0)}
                      </td>
                    )}
                    <td style={{ ...cell, fontWeight: 700 }}>
                      {formatCents(l.line_total_cents ?? 0)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Totals */}
          <div
            className="invoice-avoid"
            style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8, paddingTop: 44 }}
          >
            <div style={{ width: 330, maxWidth: '100%', flex: 'none' }}>
              <TotalRow label="Hammer subtotal" value={formatCents(hammerCents)} />
              <TotalRow
                label={premiumPct ? `Buyer's premium (${premiumPct}%)` : "Buyer's premium"}
                value={formatCents(premiumCents)}
              />
              {shippingCents > 0 && (
                <TotalRow label="Shipping" value={formatCents(shippingCents)} />
              )}
              <TotalRow
                label={taxPct ? `Sales tax (${taxPct}%)` : 'Sales tax'}
                value={formatCents(taxCents)}
              />
              {!paid ? (
                <div style={totalTailStyle}>
                  <span style={{ font: `700 14px ${HEAD}` }}>Amount due</span>
                  <span
                    style={{
                      font: `800 24px ${HEAD}`,
                      letterSpacing: '-0.02em',
                      fontVariantNumeric: 'tabular-nums',
                      color: statusColor,
                    }}
                  >
                    {formatCents(grandCents)}
                  </span>
                </div>
              ) : (
                <>
                  <div style={totalTailStyle}>
                    <span style={{ font: `600 13px ${HEAD}` }}>Total</span>
                    <span style={{ font: `600 14px ${HEAD}`, fontVariantNumeric: 'tabular-nums' }}>
                      {formatCents(grandCents)}
                    </span>
                  </div>
                  <TotalRow
                    label="Payment received"
                    value={`−${formatCents(grandCents)}`}
                    color={statusColor}
                  />
                  <div
                    style={{
                      ...totalTailStyle,
                      marginTop: 6,
                      paddingTop: 12,
                      borderTop: '1px solid var(--border, #e5e5e5)',
                    }}
                  >
                    <span style={{ font: `700 14px ${HEAD}` }}>Balance due</span>
                    <span
                      style={{
                        font: `800 24px ${HEAD}`,
                        letterSpacing: '-0.02em',
                        fontVariantNumeric: 'tabular-nums',
                        color: statusColor,
                      }}
                    >
                      {formatCents(0)}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Pickup + Pay. paddingTop (not marginTop) so the breathing room
              survives when this row starts a new printed page (margins are
              dropped at a page break under Margins:None; padding is kept). */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 22, paddingTop: 44 }}>
            {pickup && (
              <div className="invoice-avoid" style={cardStyle}>
                <CardHeading icon="warehouse">Pickup &amp; removal</CardHeading>
                {pickup.location && (
                  <div style={{ fontSize: 12.5, fontWeight: 600 }}>{pickup.location}</div>
                )}
                {pickup.address && hasAnyAddressField(pickup.address) && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted, #525252)', marginTop: 2 }}>
                    {addressOneLine(pickup.address)}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', marginTop: 12 }}>
                  {pickup.dates_text && <MetaSmall label="Hours" value={pickup.dates_text} />}
                </div>
                {pickup.instructions && (
                  <div
                    style={{
                      fontSize: 11.5,
                      lineHeight: 1.55,
                      color: 'var(--text-muted, #525252)',
                      marginTop: 12,
                    }}
                  >
                    {pickup.instructions}
                  </div>
                )}
              </div>
            )}

            <div className="invoice-avoid" style={cardStyle}>
              <CardHeading icon="card">How to pay</CardHeading>
              {!paid ? (
                <>
                  <div
                    style={{ fontSize: 12, color: 'var(--text-muted, #525252)', lineHeight: 1.55 }}
                  >
                    <span style={{ color: 'var(--text, #0a0a0a)', fontWeight: 600 }}>
                      Card or bank transfer
                    </span>{' '}
                    — sign in to your bidder account and open this invoice to pay online.
                  </div>
                  {settings.check_instructions && (
                    <div
                      style={{
                        fontSize: 12,
                        color: 'var(--text-muted, #525252)',
                        lineHeight: 1.55,
                        marginTop: 9,
                      }}
                    >
                      <span style={{ color: 'var(--text, #0a0a0a)', fontWeight: 600 }}>Check</span>{' '}
                      — {settings.check_instructions} Write invoice #{invoice.invoice_number} on the
                      memo line.
                    </div>
                  )}
                  <div
                    style={{ fontSize: 11.5, color: 'var(--text-muted, #525252)', marginTop: 11 }}
                  >
                    Due by {fmtDate(invoice.due_at)}
                    {supportEmail ? ` · ${supportEmail}` : ''}
                  </div>
                </>
              ) : (
                <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
                  <span
                    style={{
                      color: statusColor,
                      display: 'inline-flex',
                      flex: 'none',
                      marginTop: 1,
                    }}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="9" />
                      <path d="M8.5 12.5l2.4 2.4 4.6-5" />
                    </svg>
                  </span>
                  <div>
                    <div style={{ font: `700 13.5px ${HEAD}`, color: statusColor }}>
                      Payment received — thank you
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: 'var(--text-muted, #525252)',
                        lineHeight: 1.55,
                        marginTop: 4,
                      }}
                    >
                      {`Paid ${fmtDate(invoice.paid_at)}. `}
                      {thankYou || 'Your lots are cleared for pickup — no further action needed.'}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Terms */}
          {terms.length > 0 && (
            <div
              className="invoice-avoid"
              style={{
                marginTop: 26,
                paddingTop: 32,
                borderTop: '1px solid var(--border, #e5e5e5)',
              }}
            >
              <div style={microLabel}>Terms &amp; conditions</div>
              <div
                style={{
                  columns: 2,
                  columnGap: 34,
                  fontSize: 10.5,
                  lineHeight: 1.6,
                  color: 'var(--text-muted, #525252)',
                  marginTop: 10,
                }}
              >
                {terms.map((p, i) => (
                  <p
                    key={i}
                    style={{ margin: i === terms.length - 1 ? 0 : '0 0 8px', breakInside: 'avoid' }}
                  >
                    {p}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <div
            style={{
              marginTop: 22,
              paddingTop: 28,
              borderTop: '1px solid var(--border, #e5e5e5)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 16,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ fontSize: 11, color: 'var(--text-muted, #525252)' }}>
              {houseName}
              {addressOneLine(tenantAddr) ? ` · ${addressOneLine(tenantAddr)}` : ''}
              {footerNote ? ` · ${footerNote}` : ''}
            </div>
            <div style={{ fontFamily: MONO, fontSize: 10.5, color: 'var(--text-muted, #525252)' }}>
              #{invoice.invoice_number} · powered by AuctiSync
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Small presentational pieces ─────────────────────────────────────────────

function Th({
  children,
  align = 'left',
  width,
}: {
  children: ReactNode;
  align?: 'left' | 'right';
  width?: number;
}) {
  return (
    <th
      style={{
        width,
        textAlign: align,
        padding: align === 'right' ? '0 0 10px 8px' : '0 10px 10px 0',
        font: `600 10px/1.3 ${HEAD}`,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        color: 'var(--text-muted, #525252)',
        borderBottom: '1.5px solid var(--border-strong, #d4d4d4)',
      }}
    >
      {children}
    </th>
  );
}

function TotalRow({ label, value, color }: { label: ReactNode; value: ReactNode; color?: string }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 16,
        padding: '6px 0',
        fontSize: 12.5,
        color: 'var(--text-muted, #525252)',
      }}
    >
      <span>{label}</span>
      <span
        style={{ ...money, fontWeight: 500, fontSize: 13, color: color ?? 'var(--text, #0a0a0a)' }}
      >
        {value}
      </span>
    </div>
  );
}

function HeaderMeta({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <div
        style={{
          font: `600 9.5px/1.4 ${HEAD}`,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: 'var(--text-muted, #525252)',
        }}
      >
        {label}
      </div>
      <div style={{ font: `600 12.5px ${HEAD}`, marginTop: 3 }}>{value}</div>
    </div>
  );
}

function MetaBlock({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <div
        style={{
          font: `600 9.5px/1.4 ${HEAD}`,
          letterSpacing: '0.09em',
          textTransform: 'uppercase',
          color: 'var(--text-muted, #525252)',
        }}
      >
        {label}
      </div>
      <div style={{ font: `600 12.5px ${HEAD}`, marginTop: 3 }}>{value}</div>
    </div>
  );
}

function MetaSmall({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <div
        style={{
          font: `600 9.5px/1.4 ${HEAD}`,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--text-muted, #525252)',
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 12, marginTop: 2 }}>{value}</div>
    </div>
  );
}

function CardHeading({ icon, children }: { icon: 'warehouse' | 'card'; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 11 }}>
      <span style={{ color: 'var(--brand-primary, #0a0045)', display: 'inline-flex' }}>
        {icon === 'warehouse' ? (
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 21h18M5 21V8l7-4 7 4v13" />
            <path d="M9 21v-6h6v6" />
          </svg>
        ) : (
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="2" y="5" width="20" height="14" rx="2.5" />
            <path d="M2 10h20" />
          </svg>
        )}
      </span>
      <span style={{ font: `700 13px ${HEAD}`, letterSpacing: '-0.01em' }}>{children}</span>
    </div>
  );
}

function AddressLines({ address }: { address: AddressBlob }) {
  const cityLine = [address.city, address.region, address.postal_code]
    .filter((s) => Boolean(s && s.trim()))
    .join(', ')
    .replace(/, ([^,]*)$/, ' $1');
  return (
    <>
      {address.line1 ? <div>{address.line1}</div> : null}
      {address.line2 ? <div>{address.line2}</div> : null}
      {cityLine ? <div>{cityLine}</div> : null}
      {address.country ? <div>{address.country}</div> : null}
    </>
  );
}

function addressOneLine(a: AddressBlob): string {
  const cityLine = [a.city, a.region, a.postal_code]
    .filter((s) => Boolean(s && s.trim()))
    .join(', ')
    .replace(/, ([^,]*)$/, ' $1');
  return [a.line1, a.line2, cityLine, a.country]
    .filter((s) => Boolean(s && String(s).trim()))
    .join(', ');
}

function splitParagraphs(text: string | null | undefined): string[] {
  if (!text || !text.trim()) return [];
  return text
    .split(/\n{2,}|\r\n{2,}/)
    .map((p) => p.replace(/\s*\n\s*/g, ' ').trim())
    .filter(Boolean);
}

// ─── Pickup resolution ───────────────────────────────────────────────────────

interface ResolvedPickup {
  location?: string | undefined;
  address?: AddressBlob | undefined;
  instructions?: string | undefined;
  dates_text?: string | undefined;
}

function resolvePickup(
  invoice: Invoice,
  auction: Auction | null,
  fallback: InvoiceSettingsBlob['pickup_default'],
): ResolvedPickup | null {
  const clean = (v: string | null | undefined) => (v && v.trim() ? v : undefined);
  const invoiceSummary = clean(invoice.pickup_summary);
  const auctionAddress: AddressBlob = {
    line1: clean(auction?.pickup_address_line1),
    line2: clean(auction?.pickup_address_line2),
    city: clean(auction?.pickup_city),
    region: clean(auction?.pickup_region),
    postal_code: clean(auction?.pickup_postal_code),
    country: clean(auction?.pickup_country),
  };
  const fallbackAddress = fallback?.address ?? {};
  const resolved: ResolvedPickup = {
    location: invoiceSummary ?? clean(auction?.pickup_location) ?? clean(fallback?.location),
    address: {
      line1: auctionAddress.line1 ?? clean(fallbackAddress.line1),
      line2: auctionAddress.line2 ?? clean(fallbackAddress.line2),
      city: auctionAddress.city ?? clean(fallbackAddress.city),
      region: auctionAddress.region ?? clean(fallbackAddress.region),
      postal_code: auctionAddress.postal_code ?? clean(fallbackAddress.postal_code),
      country: auctionAddress.country ?? clean(fallbackAddress.country),
    },
    instructions: clean(auction?.pickup_instructions) ?? clean(fallback?.instructions),
    dates_text: clean(fallback?.dates_text),
  };
  const hasAny =
    Boolean(resolved.location) ||
    Boolean(resolved.instructions) ||
    Boolean(resolved.dates_text) ||
    (resolved.address ? hasAnyAddressField(resolved.address) : false);
  return hasAny ? resolved : null;
}

function hasAnyAddressField(a: AddressBlob): boolean {
  return Boolean(a.line1 || a.line2 || a.city || a.region || a.postal_code || a.country);
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const deskStyle: CSSProperties = {
  minHeight: '100vh',
  boxSizing: 'border-box',
  padding: '40px 24px',
  background: 'var(--gray-100, #f5f5f5)',
  color: 'var(--text, #0a0a0a)',
  fontFamily: "var(--font-sans, 'Inter', system-ui, sans-serif)",
  WebkitFontSmoothing: 'antialiased',
};

const sheetStyle: CSSProperties = {
  width: 816,
  maxWidth: '100%',
  margin: '0 auto',
  background: '#fff',
  border: '1px solid var(--border, #e5e5e5)',
  borderRadius: 14,
  boxShadow: '0 18px 50px -20px rgba(10,26,58,0.28)',
  overflow: 'hidden',
};

const headerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 24,
  padding: '26px 40px',
  // White header — no colored band. Saves printer ink and removes the need to
  // print borderless (Margins:None), so continuation pages get normal margins.
  // A thin brand-accent rule keeps a touch of brand identity at negligible ink.
  background: '#ffffff',
  color: 'var(--text, #0a0a0a)',
  borderBottom: '2px solid var(--brand-accent, #2264dc)',
};

const markStyle: CSSProperties = {
  width: 46,
  height: 46,
  borderRadius: 12,
  flex: 'none',
  display: 'grid',
  placeItems: 'center',
  background: 'var(--brand-primary, #0a0045)',
  border: '1px solid transparent',
  color: '#fff',
  font: `700 21px ${HEAD}`,
};

const amountBoxStyle: CSSProperties = {
  flex: '0 0 244px',
  maxWidth: '100%',
  border: '1px solid var(--border, #e5e5e5)',
  borderRadius: 12,
  overflow: 'hidden',
  background: '#fff',
};

const sigLabel: CSSProperties = {
  font: `600 9px/1.4 ${HEAD}`,
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
  color: 'var(--text-muted, #525252)',
};

const pickupBandStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 20,
  flexWrap: 'wrap',
  marginBottom: 20,
  padding: '14px 20px',
  border: '1px solid var(--border, #e5e5e5)',
  borderLeft: `5px solid ${PICKUP_RED}`,
  borderRadius: 12,
  background: 'rgba(220,38,38,0.045)',
};

const stripStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '16px 34px',
  padding: '13px 18px',
  border: '1px solid var(--border, #e5e5e5)',
  borderRadius: 10,
  background: 'var(--gray-50, #fafafa)',
  marginBottom: 22,
};

const cardStyle: CSSProperties = {
  border: '1px solid var(--border, #e5e5e5)',
  borderRadius: 12,
  padding: '17px 20px',
};

const totalTailStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: 16,
  marginTop: 8,
  paddingTop: 13,
  borderTop: '1.5px solid var(--border-strong, #d4d4d4)',
};

const PRINT_CSS = `
.invoice-desk { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
/* Continuation pages (2+) get a generous top margin so content isn't crammed
   against the paper edge; page 1 keeps a slimmer top so the header sits high. */
@page { size: letter; margin: 0.6in 0 0.45in; }
@page :first { margin-top: 0.4in; }
@media print {
  /* Global: hide the print-route toolbar (a sibling of the sheet). */
  .no-print { display: none !important; }
  .invoice-desk { background: #fff !important; padding: 0 !important; min-height: 0 !important; }
  .invoice-sheet { width: auto !important; max-width: none !important; margin: 0 !important; border: none !important; border-radius: 0 !important; box-shadow: none !important; }
  .invoice-sheet, .invoice-sheet * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .invoice-sheet thead { display: table-header-group; }
  .invoice-row, .invoice-avoid { break-inside: avoid; }
}
`;
